import * as React from 'react';

import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxConversationContextValue } from 'bones/constants/voicebox/conversation';
import { VoiceboxRequestMessage } from 'bones/constants/voicebox/message';
import { VoiceboxSchema } from 'bones/constants/voicebox/schema';
import {
  DEFAULT_VOICEBOX_SETTINGS,
  VoiceboxNamedGraphs,
  VoiceboxSettings,
  VoiceboxSettingsContextValue,
} from 'bones/constants/voicebox/settings';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { usePrevious } from 'bones/utils/hooks/usePrevious';
import { isEqual, isNotEqual } from 'bones/utils/isEqual';
import { noop } from 'bones/utils/noop';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';
import { alphaSorter } from 'bones/utils/sort/alphaSort';
import { getSettingsFromRequestMessage } from 'bones/utils/voicebox/getSettingsFromRequestMessage';
import { usePortalVoiceboxCatalogProvidersMissing } from 'bones/utils/voicebox/usePortalVoiceboxCatalogProvidersMissing';
import { usePortalVoiceboxDatabases } from 'bones/utils/voicebox/usePortalVoiceboxDatabases';
import {
  usePortalVoiceboxDescription,
  usePortalVoiceboxDescriptions,
} from 'bones/utils/voicebox/usePortalVoiceboxDescription';
import { usePortalVoiceboxLastSettings } from 'bones/utils/voicebox/usePortalVoiceboxLastSettings';
import { usePortalVoiceboxDatabaseModel } from 'bones/utils/voicebox/usePortalVoiceboxModel';
import {
  usePortalVoiceboxDatabaseNamedGraphs,
  usePortalVoiceboxDatabasesNamedGraphs,
} from 'bones/utils/voicebox/usePortalVoiceboxNamedGraphs';

export const DEFAULT_VOICEBOX_SETTINGS_CONTEXT_VALUE: VoiceboxSettingsContextValue =
  {
    catalogProvidersLoading: false,
    catalogProvidersMissing: false,
    databasesById: {},
    databasesLoading: false,
    descriptionsByDatabaseModel: {},
    descriptionsLoading: false,
    modelsByDatabaseId: {},
    modelsLoading: false,
    namedGraphFilter: '',
    namedGraphsByDatabaseId: {},
    namedGraphsLoading: false,
    setNamedGraphFilter: noop,
    setSettings: noop,
    settings: DEFAULT_VOICEBOX_SETTINGS,
    lastSettingsLoading: false,
    lastSettingsRefetching: false,
  };

export const VoiceboxSettingsContext =
  React.createContext<VoiceboxSettingsContextValue>(
    DEFAULT_VOICEBOX_SETTINGS_CONTEXT_VALUE
  );

export const useVoiceboxSettings = () => {
  return React.useContext(VoiceboxSettingsContext);
};

export const useVoiceboxSettingsValue = ({
  id,
  conversation: { isLoadingConversation, isRefetchingConversation, messages },
  schema: {
    config: {
      catalogDatabaseId,
      connectionId,
      connectionIdx,
      defaultSettings,
      initialSettings,
    },
    settingsAside: { useSettings },
  },
}: {
  /**
   * the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  id?: string | null;
  conversation: VoiceboxConversationContextValue;
  schema: VoiceboxSchema;
}): VoiceboxSettingsContextValue => {
  const databasesQuery = usePortalVoiceboxDatabases(connectionIdx, {
    retry: false,
  });
  const databasesById = React.useMemo(
    () => databasesQuery.data || {},
    [databasesQuery]
  );
  const databasesLoading = databasesQuery.isLoading;

  const lastSettings = usePortalVoiceboxLastSettings(connectionIdx, {
    enabled: useSettings,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
  const lastSettingsLoading = lastSettings.isLoading;
  const lastSettingsRefetching = lastSettings.isRefetching;

  const didIdChange = useDidValueChange(id);
  const needsRefetch = didIdChange && id && useSettings;
  React.useEffect(() => {
    if (needsRefetch) {
      lastSettings.refetch();
    }
  }, [lastSettings, needsRefetch]);

  const [settings, setSettings] = React.useState<VoiceboxSettings>(
    initialSettings || defaultSettings
  );

  const fetching =
    isLoadingConversation ||
    isRefetchingConversation ||
    lastSettingsLoading ||
    lastSettingsRefetching;
  const didFetchingChange = useDidValueChange(fetching);
  React.useEffect(() => {
    // load the last settings
    if (!initialSettings && didFetchingChange && !fetching) {
      const lastMessage = messages
        .slice()
        .reverse()
        .find((message): message is VoiceboxRequestMessage =>
          Boolean(message.isRequest)
        );
      if (lastMessage) {
        setSettings(
          getSettingsFromRequestMessage(lastMessage, defaultSettings)
        );
        return;
      }
      if (lastSettings.data) {
        setSettings({
          ...defaultSettings,
          ...lastSettings.data,
        });
        return;
      }
    }

    // use the first database when there is none
    if (useSettings && !fetching && !settings.databaseId) {
      const firstNonCatalogDatabaseId = Object.keys(databasesById)
        .filter((databaseId) => databaseId !== catalogDatabaseId)
        .sort(alphaSorter)[0];
      const firstDatabase =
        databasesById[
          firstNonCatalogDatabaseId ||
            catalogDatabaseId ||
            /* istanbul ignore next */ ''
        ];

      // istanbul ignore else
      if (firstDatabase) {
        const firstModel = [...firstDatabase.models].sort(alphaSorter)[0] || '';
        setSettings({
          databaseId: firstDatabase.id,
          model: firstModel,
          namedGraphs: defaultSettings.namedGraphs,
          reasoning: defaultSettings.reasoning,
        });
      }
    }
  }, [
    catalogDatabaseId,
    databasesById,
    defaultSettings,
    didFetchingChange,
    fetching,
    initialSettings,
    lastSettings,
    messages,
    settings,
    useSettings,
  ]);

  const [descriptionsByDatabaseModel, setDescriptionsByDatabaseModel] =
    React.useState<Record<string, Record<string, string>>>({});

  const [modelsByDatabaseId, setModelsByDatabaseId] = React.useState<
    Record<string, Record<string, QueryBuilderModel>>
  >({});

  const [namedGraphsByDatabaseId, setNamedGraphsByDatabaseId] = React.useState<
    Record<string, VoiceboxNamedGraphs>
  >({});

  const initialDatabaseIds = new Set<string>();
  const initialDatabaseModels = new Set<string>();
  messages.forEach((message) => {
    if (message.isRequest && message.context.database_id) {
      initialDatabaseIds.add(message.context.database_id);
      initialDatabaseModels.add(
        `${message.context.database_id} ${
          message.context.model || DEFAULT_MODEL_NAME
        }`
      );
    }
  });
  const allDatabaseIds = new Set([
    ...initialDatabaseIds,
    ...Object.keys(databasesById),
  ]);

  const catalogProvidersMissingQuery = usePortalVoiceboxCatalogProvidersMissing(
    connectionIdx,
    catalogDatabaseId,
    {
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );
  const catalogProvidersLoading = catalogProvidersMissingQuery.isLoading;
  const catalogProvidersMissing = Boolean(catalogProvidersMissingQuery.data);

  const descriptionQuery = usePortalVoiceboxDescription(
    connectionId,
    connectionIdx,
    settings.databaseId,
    settings.model || DEFAULT_MODEL_NAME,
    {
      enabled: allDatabaseIds.has(settings.databaseId),
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );
  const descriptionsLoading = descriptionQuery.isLoading;
  React.useEffect(() => {
    if (!descriptionQuery.data) {
      return;
    }

    const { databaseId, model, description } = descriptionQuery.data;
    setDescriptionsByDatabaseModel((prev) => {
      const next = {
        ...prev,
        [databaseId]: { ...prev[databaseId], [model]: description },
      };
      return isEqual(prev, next) ? prev : next;
    });
  }, [descriptionQuery]);

  const descriptionQueries = usePortalVoiceboxDescriptions(
    connectionId,
    connectionIdx,
    Array.from(initialDatabaseModels).map(
      (key) => key.split(' ') as [string, string]
    ),
    () => ({
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    })
  );
  React.useEffect(() => {
    const descriptions = descriptionQueries
      .map((query) => query.data)
      .filter(
        (
          data
        ): data is { databaseId: string; model: string; description: string } =>
          Boolean(data)
      );

    if (!descriptions.length) {
      return;
    }

    setDescriptionsByDatabaseModel((prev) => {
      const next = { ...prev };
      descriptions.forEach(({ databaseId, model, description }) => {
        if (!next[databaseId]) {
          next[databaseId] = {};
        }
        next[databaseId][model] = description;
      });
      return isEqual(prev, next) ? prev : next;
    });
  }, [descriptionQueries]);

  const databaseModelQuery = usePortalVoiceboxDatabaseModel(
    connectionIdx,
    settings.databaseId,
    settings.model || DEFAULT_MODEL_NAME,
    databasesById[settings.databaseId]?.modelGraphs[
      settings.model || DEFAULT_MODEL_NAME
    ],
    {
      enabled: allDatabaseIds.has(settings.databaseId),
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );
  const modelsLoading = databaseModelQuery.isLoading;
  React.useEffect(() => {
    if (!databaseModelQuery.data) {
      return;
    }

    const { databaseId, model, results } = databaseModelQuery.data;
    setModelsByDatabaseId((prev) => {
      const next = {
        ...prev,
        [databaseId]: { ...prev[databaseId], [model]: results },
      };
      return isEqual(prev, next) ? prev : next;
    });
  }, [databaseModelQuery]);

  const [namedGraphFilter, setNamedGraphFilter] = React.useState('');

  const databaseNamedGraphsQuery = usePortalVoiceboxDatabaseNamedGraphs(
    connectionIdx,
    settings.databaseId,
    namedGraphFilter,
    {
      enabled: useSettings && allDatabaseIds.has(settings.databaseId),
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );
  React.useEffect(() => {
    if (!databaseNamedGraphsQuery?.data) {
      return;
    }

    const { databaseId, namedGraphs } = databaseNamedGraphsQuery.data;
    setNamedGraphsByDatabaseId((prev) => {
      const next = {
        ...prev,
        [databaseId]: { ...prev[databaseId], ...namedGraphs },
      };
      return isEqual(prev, next) ? prev : next;
    });
  }, [databaseNamedGraphsQuery]);

  const databaseNamedGraphsQueries = usePortalVoiceboxDatabasesNamedGraphs(
    connectionIdx,
    Array.from(allDatabaseIds),
    () => ({
      enabled: useSettings,
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    })
  );
  const namedGraphsLoading =
    databaseNamedGraphsQuery.isLoading ||
    databaseNamedGraphsQueries.some((query) => query.isLoading);
  React.useEffect(() => {
    const databaseNamedGraphsById = databaseNamedGraphsQueries
      .map((query) => query.data)
      .filter(
        (
          data
        ): data is { databaseId: string; namedGraphs: VoiceboxNamedGraphs } =>
          Boolean(data && Object.keys(data.namedGraphs).length)
      )
      .reduce<Record<string, VoiceboxNamedGraphs>>(
        (acc, { databaseId, namedGraphs }) => {
          acc[databaseId] = namedGraphs;
          return acc;
        },
        {}
      );

    const databaseNamedGraphs = Object.entries(databaseNamedGraphsById);
    if (!databaseNamedGraphs.length) {
      return;
    }

    setNamedGraphsByDatabaseId((prev) => {
      const next = {
        ...prev,
        ...databaseNamedGraphs.reduce<Record<string, VoiceboxNamedGraphs>>(
          (acc, [databaseId, namedGraphs]) => {
            if (!prev[databaseId]) {
              acc[databaseId] = namedGraphs;
            }
            return acc;
          },
          {}
        ),
      };
      return isEqual(prev, next) ? prev : next;
    });
  }, [databaseNamedGraphsQueries]);

  const prevInitialSettings = usePrevious(initialSettings);
  const didInitialSettingsChange = useDidValueChange(initialSettings, isEqual);
  const prevConnectionId = usePrevious(connectionId);
  const didConnectionIdChange = useDidValueChange(connectionId);

  React.useEffect(() => {
    if (
      (prevConnectionId !== undefined && didConnectionIdChange) ||
      (didInitialSettingsChange &&
        initialSettings &&
        isNotEqual(settings, initialSettings))
    ) {
      setDescriptionsByDatabaseModel({});
      setModelsByDatabaseId({});
      setNamedGraphFilter('');
      setNamedGraphsByDatabaseId({});
      setSettings(initialSettings || defaultSettings);
    }
  }, [
    defaultSettings,
    didConnectionIdChange,
    didInitialSettingsChange,
    initialSettings,
    prevConnectionId,
    prevInitialSettings,
    settings,
  ]);

  return {
    catalogProvidersLoading,
    catalogProvidersMissing,
    databasesById,
    databasesLoading,
    descriptionsByDatabaseModel,
    descriptionsLoading,
    modelsByDatabaseId,
    modelsLoading,
    namedGraphFilter,
    namedGraphsByDatabaseId,
    namedGraphsLoading,
    setNamedGraphFilter,
    setSettings,
    settings,
    lastSettingsLoading,
    lastSettingsRefetching,
  };
};
